import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, ArrowRight, KeyRound } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable phone-OTP login widget.
 * Props:
 *   purpose: 'admin' | 'applicant' | 'parent'
 *   onSuccess({ access_token, role, user })
 *   allowSignup?: bool  (defaults true — collects first/last name if signup_required)
 */
const PhoneOtpLogin = ({ purpose = "parent", onSuccess, allowSignup = true }) => {
  const [step, setStep] = useState(1); // 1=phone, 2=code, 3=name
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState("");

  const send = async () => {
    if ((phone.replace(/\D/g, "") || "").length < 7) {
      toast.error("Enter a valid phone number (with country code).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/otp/send`, { phone, purpose });
      if (data.dev_code) {
        setDevCode(data.dev_code);
        toast.info(`Dev mode: your code is ${data.dev_code}`);
      } else {
        toast.success("Code sent by SMS.");
      }
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not send code");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length < 4) { toast.error("Enter the code from your text."); return; }
    setBusy(true);
    try {
      const payload = { phone, code, purpose };
      if (step === 3) { payload.first_name = first; payload.last_name = last; }
      const { data } = await axios.post(`${API}/auth/otp/verify`, payload);
      if (data.signup_required && allowSignup) {
        toast.info("Almost there — tell us your name.");
        setDevCode("");           // clear old code display; new one was auto-sent
        setStep(3);
        setCode("");
        return;
      }
      onSuccess?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`otp-login-${purpose}`} className="space-y-4">
      {step === 1 && (
        <>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mobile number</Label>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-11 w-11 rounded-lg border border-border flex items-center justify-center text-muted-foreground shrink-0">
                <Phone size={16} />
              </div>
              <Input
                data-testid={`otp-phone-${purpose}`}
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 010 1234"
                className="h-11 rounded-lg"
                autoComplete="tel"
              />
            </div>
          </div>
          <Button data-testid={`otp-send-${purpose}`} disabled={busy} onClick={send} className="w-full rounded-full h-12 bg-primary text-primary-foreground">
            {busy ? "Sending…" : (<>Text me a code <ArrowRight size={14} className="ml-1.5" /></>)}
          </Button>
        </>
      )}

      {(step === 2 || step === 3) && (
        <>
          {step === 3 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">First name</Label>
                <Input data-testid={`otp-first-${purpose}`} value={first} onChange={(e) => setFirst(e.target.value)} className="mt-2 h-11 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Last name</Label>
                <Input data-testid={`otp-last-${purpose}`} value={last} onChange={(e) => setLast(e.target.value)} className="mt-2 h-11 rounded-lg" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">6-digit code sent to {phone}</Label>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-11 w-11 rounded-lg border border-border flex items-center justify-center text-muted-foreground shrink-0"><KeyRound size={16} /></div>
              <Input
                data-testid={`otp-code-${purpose}`}
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="123456"
                className="h-11 rounded-lg font-mono tracking-widest text-lg"
                autoComplete="one-time-code"
              />
            </div>
            {devCode && <div className="mt-2 text-xs text-muted-foreground">Dev / staging code: <span className="font-mono text-primary">{devCode}</span></div>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid={`otp-back-${purpose}`} onClick={() => { setStep(1); setCode(""); setDevCode(""); }}>Change number</Button>
            <Button data-testid={`otp-verify-${purpose}`} disabled={busy} onClick={verify} className="flex-1 rounded-full h-11 bg-primary text-primary-foreground">
              {busy ? "Verifying…" : "Verify & sign in"}
            </Button>
          </div>
          <button className="text-xs text-muted-foreground hover:text-primary" data-testid={`otp-resend-${purpose}`} onClick={send}>
            Didn't get it? Send another code
          </button>
        </>
      )}
    </div>
  );
};

export default PhoneOtpLogin;
